import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Search,
    Filter,
    Download,
    ChevronUp,
    ChevronDown,
    User,
    Maximize2,
    X,
    Mail,
    Users,
    Grid3X3,
    Plus,
    Minus,
    Copy,
    GitBranch,
    Minimize2,
    UserX
} from 'lucide-react';
import { useEmployees } from '../../../contexts/EmployeeContext';
import functionalStructureService from '../../../services/functionalStructure.service';

const Organization = ({ readOnly = false }) => {
    const { employees, departments } = useEmployees();
    const navigate = useNavigate();
    const location = useLocation();
    const [viewAs, setViewAs] = useState('Admin');
    const [detailedView, setDetailedView] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [expandedNodes, setExpandedNodes] = useState({});
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [hoveredEmployee, setHoveredEmployee] = useState(null);
    const [copiedEmail, setCopiedEmail] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [showTopExpand, setShowTopExpand] = useState(true);
    const [showQuickProfile, setShowQuickProfile] = useState(false);
    const [quickProfileView, setQuickProfileView] = useState('email');
    const [showDetailedProfile, setShowDetailedProfile] = useState(false);
    const [showMatrixModal, setShowMatrixModal] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [highlightedEmployee, setHighlightedEmployee] = useState(null);
    const [employeeNotFound, setEmployeeNotFound] = useState(false);
    const [selectedLegalEntity, setSelectedLegalEntity] = useState('all');
    const chartContainerRef = useRef(null);
    const fullscreenRef = useRef(null);

    // Load legal entities from functional structure backend and employee data
    const [legalEntities, setLegalEntities] = useState([]);
    const [loadingStructure, setLoadingStructure] = useState(false);

    useEffect(() => {
        fetchLegalEntities();
    }, [employees]);

    const fetchLegalEntities = async () => {
        const authToken = localStorage.getItem('authToken');
        let entities = [];

        // Load from localStorage first
        const saved = localStorage.getItem('functionalStructure');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                entities = parsed.legalEntities || [];
            } catch (e) {
                console.error('Error loading from localStorage:', e);
            }
        }

        // Fetch from backend if authenticated
        if (authToken) {
            try {
                setLoadingStructure(true);
                const data = await functionalStructureService.getActive();

                if (data && data.structure_data) {
                    entities = data.structure_data.legalEntities || [];
                }
            } catch (error) {
                console.error('Error fetching functional structure:', error);
            } finally {
                setLoadingStructure(false);
            }
        }

        // Also extract unique legal entities from employee data
        const employeeLegalEntities = new Set();
        employees.forEach(emp => {
            const legalEntity = emp.legalEntity || emp.legal_entity;
            if (legalEntity && legalEntity !== 'Not specified') {
                employeeLegalEntities.add(legalEntity);
            }
        });

        // Merge with existing entities, avoiding duplicates
        const employeeEntities = Array.from(employeeLegalEntities).map(name => {
            // Check if entity already exists in functional structure
            const existing = entities.find(e => e.name === name);
            if (existing) return existing;
            // Create new entity object
            return {
                id: name.toLowerCase().replace(/\s+/g, '-'),
                name: name
            };
        });

        // Combine and deduplicate
        const allEntities = [...entities];
        employeeEntities.forEach(empEntity => {
            if (!allEntities.find(e => e.id === empEntity.id)) {
                allEntities.push(empEntity);
            }
        });

        setLegalEntities(allEntities);
    };

    // Calculate actual total reportees
    const calculateTotalReportees = (employee) => {
        let total = 0;
        if (employee.subordinates && employee.subordinates.length > 0) {
            employee.subordinates.forEach(sub => {
                total += 1; // Count the subordinate
                total += calculateTotalReportees(sub); // Count their subordinates
            });
        }
        return total;
    };

    // Build organization hierarchy from live employee data
    const buildOrganizationHierarchy = () => {
        //console.log('🔍 === ORG VIEW FILTER DEBUG ===');
        //console.log('Selected Legal Entity ID:', selectedLegalEntity);
        //console.log('Total employees in system:', employees.length);

        // Filter employees by selected legal entity
        const filteredEmployees = selectedLegalEntity === 'all'
            ? employees
            : employees.filter(emp => {
                const empLegalEntity = emp.legalEntity || emp.legal_entity;

                // Include employees with no legal entity
                if (!empLegalEntity || empLegalEntity === 'Not specified' || empLegalEntity === '') {
                    return true;
                }

                // Find the entity name from the ID
                const selectedEntityName = legalEntities.find(le => le.id === selectedLegalEntity)?.name;
                return empLegalEntity === selectedEntityName || empLegalEntity === selectedLegalEntity;
            });

        //console.log('Filtered employees count:', filteredEmployees.length);
        // //console.log('Filtered employee names:', filteredEmployees.map(emp => ({
        //     name: emp.name || `${emp.first_name} ${emp.last_name}`,
        //     legalEntity: emp.legalEntity || emp.legal_entity,
        //     managerId: emp.managerId || emp.reporting_manager_id,
        //     id: emp.id
        // })));

        if (filteredEmployees.length === 0) {
            //console.log('❌ No employees found for selected legal entity');
            return {
                name: selectedLegalEntity === 'all' ? 'Your Organization' : legalEntities.find(le => le.id === selectedLegalEntity)?.name || 'Your Organization',
                ceo: null,
                peers: [],
                additionalRoles: []
            };
        }

        // Convert employee to organization node format
        const convertEmployeeToNode = (employee) => {
            // Check both managerId and reporting_manager_id fields
            const subordinates = filteredEmployees.filter(emp =>
                (emp.managerId === employee.id) ||
                (emp.reporting_manager_id === employee.id)
            );

            // Find reporting manager
            const managerId = employee.managerId || employee.reporting_manager_id;
            const reportingManager = managerId ? filteredEmployees.find(emp => emp.id === managerId) : null;

            const node = {
                id: employee.id,
                name: employee.name || `${employee.first_name} ${employee.last_name}`,
                role: employee.designation || 'Employee',
                department: employee.department || 'Unassigned',
                legalEntity: employee.legalEntity || employee.legal_entity || 'Not specified',
                directReports: subordinates.length,
                get totalReports() { return calculateTotalReportees(this); },
                avatar: employee.photo || null,
                email: employee.email,
                employeeId: employee.employee_code,
                dateOfJoining: employee.joining_date || employee.joiningDate || 'Not specified',
                officeLocation: employee.work_location || 'Office',
                reportingManager: reportingManager ? {
                    id: reportingManager.id,
                    name: reportingManager.name || `${reportingManager.first_name} ${reportingManager.last_name}`,
                    role: reportingManager.designation || 'Employee',
                    email: reportingManager.email
                } : null,
                directReportees: subordinates.map(sub => ({
                    id: sub.id,
                    name: sub.name || `${sub.first_name} ${sub.last_name}`,
                    role: sub.designation || 'Employee',
                    email: sub.email
                })),
                matrixManager: null, // Can be extended based on requirements
                dottedLineReportees: [], // Can be extended based on requirements
                subordinates: subordinates.map(sub => convertEmployeeToNode(sub))
            };
            return node;
        };

        // Find the root employee (CEO/top-level manager - one with no manager)
        // Check both managerId and reporting_manager_id fields
        const rootEmployees = filteredEmployees.filter(emp =>
            (!emp.managerId || emp.managerId === '') &&
            (!emp.reporting_manager_id || emp.reporting_manager_id === '')
        );

        //console.log('Root employees found:', rootEmployees.length);
        // //console.log('Root employee names:', rootEmployees.map(emp => ({
        //     name: emp.name || `${emp.first_name} ${emp.last_name}`,
        //     id: emp.id,
        //     managerId: emp.managerId || emp.reporting_manager_id || 'NONE'
        // })));

        if (rootEmployees.length === 0) {
            //console.log('⚠️ No root employees found! Using first filtered employee as CEO');
            // If no clear root, take the first employee as CEO
            const ceoEmployee = filteredEmployees[0];
            //console.log('CEO selected:', ceoEmployee.name || `${ceoEmployee.first_name} ${ceoEmployee.last_name}`);
            return {
                name: selectedLegalEntity === 'all' ? 'Your Organization' : legalEntities.find(le => le.id === selectedLegalEntity)?.name || 'Your Organization',
                ceo: convertEmployeeToNode(ceoEmployee),
                peers: [],
                additionalRoles: []
            };
        }

        // If multiple root employees, treat first as CEO and others as peers
        const ceoEmployee = rootEmployees[0];
        const peerEmployees = rootEmployees.slice(1);

        //console.log('✅ CEO:', ceoEmployee.name || `${ceoEmployee.first_name} ${ceoEmployee.last_name}`);
        //console.log('Peers:', peerEmployees.map(emp => emp.name || `${emp.first_name} ${emp.last_name}`));
        //console.log('=== END DEBUG ===\n');

        return {
            name: selectedLegalEntity === 'all' ? 'Your Organization' : legalEntities.find(le => le.id === selectedLegalEntity)?.name || 'Your Organization',
            ceo: convertEmployeeToNode(ceoEmployee),
            peers: peerEmployees.map(emp => convertEmployeeToNode(emp)),
            additionalRoles: [] // Can be extended for special roles
        };
    };

    // Get live organization data - memoized to rebuild when employees or selectedLegalEntity changes
    const organizationData = useMemo(() => buildOrganizationHierarchy(), [employees, selectedLegalEntity, legalEntities]);

    // Get all unique roles for filter
    const getAllRoles = () => {
        const roles = new Set();
        const traverse = (employee) => {
            if (employee && employee.role) roles.add(employee.role);
            if (employee && employee.subordinates) {
                employee.subordinates.forEach(sub => traverse(sub));
            }
        };

        // Add CEO role if exists
        if (organizationData.ceo) {
            roles.add(organizationData.ceo.role);
            organizationData.ceo.subordinates.forEach(sub => traverse(sub));
        }

        // Add peer roles
        organizationData.peers.forEach(peer => {
            roles.add(peer.role);
            traverse(peer);
        });

        // Add additional roles
        organizationData.additionalRoles.forEach(role => {
            roles.add(role.role);
            traverse(role);
        });

        return Array.from(roles).sort();
    };

    // Filter employees based on selected filters
    const isEmployeeVisible = (employee) => {
        if (selectedFilters.length === 0) return true;
        return selectedFilters.includes(employee.role);
    };

    // Handle detailed view toggle
    useEffect(() => {
        if (detailedView) {
            const allNodeIds = [];
            const collectNodeIds = (employee) => {
                if (employee && employee.id) {
                    allNodeIds.push(employee.id);
                    if (employee.subordinates) {
                        employee.subordinates.forEach(sub => collectNodeIds(sub));
                    }
                }
            };

            // Expand CEO and all subordinates if CEO exists
            if (organizationData.ceo) {
                collectNodeIds(organizationData.ceo);
            }

            // Also expand additional roles and peers if they have subordinates
            organizationData.additionalRoles.forEach(role => collectNodeIds(role));
            organizationData.peers.forEach(peer => collectNodeIds(peer));

            const expandedState = {};
            allNodeIds.forEach(id => {
                expandedState[id] = true;
            });
            setExpandedNodes(expandedState);
            setShowTopExpand(true);
        } else {
            setExpandedNodes({});
            setShowTopExpand(false);
        }
    }, [detailedView]);

    // Execute search on Enter or button click
    const executeSearch = () => {
        if (searchInput.trim() === '') {
            setHighlightedEmployee(null);
            setEmployeeNotFound(false);
            setSearchQuery('');
            return;
        }

        setSearchQuery(searchInput);
        const searchLower = searchInput.toLowerCase();
        let foundEmployee = null;
        let pathToEmployee = [];

        const searchInEmployee = (employee, path = []) => {
            if (employee.name.toLowerCase().includes(searchLower)) {
                foundEmployee = employee;
                pathToEmployee = [...path];
                return true;
            }
            if (employee.subordinates) {
                for (let sub of employee.subordinates) {
                    if (searchInEmployee(sub, [...path, employee.id])) return true;
                }
            }
            return false;
        };

        let found = false;
        if (searchInEmployee(organizationData.ceo)) {
            found = true;
        } else if (organizationData.peers.some(peer => searchInEmployee(peer))) {
            found = true;
        } else if (organizationData.additionalRoles.some(role => searchInEmployee(role))) {
            found = true;
        }

        if (found && foundEmployee) {
            setEmployeeNotFound(false);
            setHighlightedEmployee(foundEmployee.id);

            if (pathToEmployee.length > 0) {
                const newExpandedState = { ...expandedNodes };
                pathToEmployee.forEach(nodeId => {
                    newExpandedState[nodeId] = true;
                });
                setExpandedNodes(newExpandedState);
                setShowTopExpand(true);
            }

            setTimeout(() => {
                const element = document.getElementById(`employee-${foundEmployee.id}`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
                }
            }, 100);
        } else {
            setEmployeeNotFound(true);
            setHighlightedEmployee(null);
        }
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            executeSearch();
        }
    };

    const toggleExpand = (employeeId, e) => {
        e?.stopPropagation();
        setExpandedNodes(prev => ({
            ...prev,
            [employeeId]: !prev[employeeId]
        }));
    };

    const handleCopyEmail = (email, e) => {
        e?.stopPropagation();
        navigator.clipboard.writeText(email);
        setCopiedEmail(true);
        setTimeout(() => setCopiedEmail(false), 2000);
    };

    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 0.1, 2));
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
    };

    const handleEmployeeCardClick = (employee, e) => {
        e?.stopPropagation();
        setSelectedEmployee(employee);
        setShowQuickProfile(true);
        setQuickProfileView('email');
    };

    const handleViewProfile = () => {
        if (!selectedEmployee) return;

        setShowQuickProfile(false);

        // Determine current portal context
        const path = location.pathname;
        let targetPath = '';

        if (path.startsWith('/admin')) {
            targetPath = `/admin/employees/${selectedEmployee.id}`;
        } else if (path.startsWith('/manager')) {
            targetPath = `/manager/view-profile/${selectedEmployee.id}`;
        } else if (path.startsWith('/employee')) {
            targetPath = `/employee/view-profile/${selectedEmployee.id}`;
        } else {
            // Default to employee portal
            targetPath = `/employee/view-profile/${selectedEmployee.id}`;
        }

        navigate(targetPath);
    };

    const handleShowMatrixView = () => {
        setShowQuickProfile(false);
        setShowMatrixModal(true);
    };

    const handleFullscreen = () => {
        if (!isFullscreen) {
            if (fullscreenRef.current?.requestFullscreen) {
                fullscreenRef.current.requestFullscreen();
                // Auto-adjust zoom to fit content in fullscreen
                setTimeout(() => {
                    if (chartContainerRef.current) {
                        const containerWidth = window.innerWidth - 64; // Account for padding
                        const contentWidth = chartContainerRef.current.scrollWidth * zoomLevel;
                        if (contentWidth > containerWidth) {
                            const newZoom = Math.max(0.5, Math.min(1, containerWidth / (contentWidth / zoomLevel)));
                            setZoomLevel(newZoom);
                        }
                    }
                }, 100);
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        }
        setIsFullscreen(!isFullscreen);
    };

    const handleDownload = () => {
        const confirmDownload = window.confirm('Do you want to download the current organization chart as PDF?');
        if (confirmDownload) {
            window.print();
        }
    };

    const toggleFilter = (role) => {
        setSelectedFilters(prev =>
            prev.includes(role)
                ? prev.filter(r => r !== role)
                : [...prev, role]
        );
    };

    const clearFilters = () => {
        setSelectedFilters([]);
    };

    // Filter Dropdown Component
    const FilterDropdown = () => {
        if (!showFilters) return null;

        const roles = getAllRoles();

        return (
            <div className="absolute top-full left-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:shadow-gray-900 border border-gray-200 dark:border-gray-600 p-4 z-20 w-64">
                <h3 className="font-medium text-gray-900 dark:text-white mb-3">Filter by Role</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                    {roles.map(role => (
                        <label key={role} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-1 rounded">
                            <input
                                type="checkbox"
                                checked={selectedFilters.includes(role)}
                                onChange={() => toggleFilter(role)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{role}</span>
                        </label>
                    ))}
                </div>
                <div className="flex justify-end mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <button
                        onClick={clearFilters}
                        className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                    >
                        Clear All
                    </button>
                </div>
            </div>
        );
    };

    // Quick Profile Modal
    const QuickProfileModal = () => {
        if (!showQuickProfile || !selectedEmployee) return null;

        return (
            <>
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                     onClick={() => setShowQuickProfile(false)} />

                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900 p-6 z-50 max-w-sm w-full">
                    <div className="text-center">
                        <div className="w-24 h-24 bg-gray-300 dark:bg-gray-700 rounded-full mx-auto mb-4 flex items-center justify-center">
                            <User className="w-12 h-12 text-gray-600 dark:text-gray-400" />
                        </div>

                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{selectedEmployee.name}</h2>

                        <p className="text-gray-600 dark:text-gray-400 mt-1">{selectedEmployee.role}</p>
                        {selectedEmployee.department && (
                            <p className="text-gray-500 dark:text-gray-400 text-sm">{selectedEmployee.department}</p>
                        )}

                        <div className="flex justify-center space-x-2 mt-6 mb-4">
                            <button
                                onClick={() => setQuickProfileView('email')}
                                className={`p-3 rounded-lg transition-all ${
                                    quickProfileView === 'email'
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                <Mail size={20} />
                            </button>
                            <button
                                onClick={() => setQuickProfileView('matrix')}
                                className={`p-3 rounded-lg transition-all ${
                                    quickProfileView === 'matrix'
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                <GitBranch size={20} />
                            </button>
                        </div>

                        {quickProfileView === 'email' ? (
                            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 shadow-inner">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-700 dark:text-gray-300">{selectedEmployee.email}</span>
                                    <button
                                        onClick={(e) => handleCopyEmail(selectedEmployee.email, e)}
                                        className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                                        title="Copy Email"
                                    >
                                        <Copy size={16} className="text-gray-600 dark:text-gray-400" />
                                    </button>
                                </div>
                                {copiedEmail && (
                                    <p className="text-xs text-green-600 dark:text-green-400 mt-2">Copied!</p>
                                )}
                            </div>
                        ) : (
                            <button
                                onClick={handleShowMatrixView}
                                className="w-full bg-gray-50 dark:bg-gray-900 rounded-lg p-4 shadow-inner hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                            >
                                <p className="text-sm text-gray-600 dark:text-gray-400">View Reporting Manager & Direct Reportees</p>
                            </button>
                        )}

                        <button
                            onClick={handleViewProfile}
                            className="mt-6 w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors font-medium"
                        >
                            GO TO PROFILE
                        </button>
                    </div>
                </div>
            </>
        );
    };

    // Matrix Manager Modal - Shows Reporting Manager and Direct Reportees
    const MatrixManagerModal = () => {
        if (!showMatrixModal || !selectedEmployee) return null;

        const reportingManagerCount = selectedEmployee.reportingManager ? 1 : 0;
        const directReporteesCount = selectedEmployee.directReportees?.length || 0;
        const matrixManagerCount = selectedEmployee.matrixManager ? 1 : 0;
        const dottedLineCount = selectedEmployee.dottedLineReportees?.length || 0;

        return (
            <>
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={() => setShowMatrixModal(false)} />

                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900 p-6 z-50 max-w-md w-full max-h-[80vh] overflow-y-auto">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{selectedEmployee.name}'s Hierarchy</h3>

                    <div className="space-y-4">
                        {/* Reporting Manager */}
                        <div>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Reporting Manager ({reportingManagerCount}):
                            </p>
                            {selectedEmployee.reportingManager ? (
                                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 flex items-center space-x-3 shadow dark:shadow-gray-900 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                     onClick={(e) => {
                                         e.stopPropagation();
                                         const manager = selectedEmployee.reportingManager;
                                         // Find the full employee node
                                         const findEmployee = (emp) => {
                                             if (emp.id === manager.id) return emp;
                                             if (emp.subordinates) {
                                                 for (let sub of emp.subordinates) {
                                                     const found = findEmployee(sub);
                                                     if (found) return found;
                                                 }
                                             }
                                             return null;
                                         };
                                         let fullManager = null;
                                         if (organizationData.ceo) fullManager = findEmployee(organizationData.ceo);
                                         if (!fullManager && organizationData.peers) {
                                             for (let peer of organizationData.peers) {
                                                 fullManager = findEmployee(peer);
                                                 if (fullManager) break;
                                             }
                                         }
                                         if (fullManager) {
                                             setSelectedEmployee(fullManager);
                                         }
                                     }}>
                                    <div className="w-10 h-10 bg-gray-300 dark:bg-gray-700 rounded-full flex items-center justify-center">
                                        <User className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium text-gray-900 dark:text-white">{selectedEmployee.reportingManager.name}</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">{selectedEmployee.reportingManager.role}</p>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500 dark:text-gray-400 italic">No reporting manager (Top level)</p>
                            )}
                        </div>

                        {/* Direct Reportees */}
                        <div>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Direct Reportees ({directReporteesCount}):
                            </p>
                            {selectedEmployee.directReportees && selectedEmployee.directReportees.length > 0 ? (
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                    {selectedEmployee.directReportees.map((reportee, idx) => (
                                        <div key={idx}
                                             className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 flex items-center space-x-3 shadow dark:shadow-gray-900 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                             onClick={(e) => {
                                                 e.stopPropagation();
                                                 // Find the full employee node
                                                 const findEmployee = (emp) => {
                                                     if (emp.id === reportee.id) return emp;
                                                     if (emp.subordinates) {
                                                         for (let sub of emp.subordinates) {
                                                             const found = findEmployee(sub);
                                                             if (found) return found;
                                                         }
                                                     }
                                                     return null;
                                                 };
                                                 let fullReportee = null;
                                                 if (organizationData.ceo) fullReportee = findEmployee(organizationData.ceo);
                                                 if (!fullReportee && organizationData.peers) {
                                                     for (let peer of organizationData.peers) {
                                                         fullReportee = findEmployee(peer);
                                                         if (fullReportee) break;
                                                     }
                                                 }
                                                 if (fullReportee) {
                                                     setSelectedEmployee(fullReportee);
                                                 }
                                             }}>
                                            <div className="w-10 h-10 bg-gray-300 dark:bg-gray-700 rounded-full flex items-center justify-center">
                                                <User className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                            </div>
                                            <div className="flex-1">
                                                <p className="font-medium text-gray-900 dark:text-white">{reportee.name}</p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">{reportee.role}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500 dark:text-gray-400 italic">No direct reportees</p>
                            )}
                        </div>

                        {/* Matrix Manager (if exists) */}
                        {matrixManagerCount > 0 && (
                            <div>
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Matrix Manager ({matrixManagerCount}):
                                </p>
                                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 flex items-center space-x-3 shadow dark:shadow-gray-900">
                                    <div className="w-10 h-10 bg-gray-300 dark:bg-gray-700 rounded-full flex items-center justify-center">
                                        <User className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">{selectedEmployee.matrixManager.name}</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">{selectedEmployee.matrixManager.role}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Dotted Line Reportees (if exists) */}
                        {dottedLineCount > 0 && (
                            <div>
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Dotted Line Reportees ({dottedLineCount}):
                                </p>
                                <div className="space-y-2">
                                    {selectedEmployee.dottedLineReportees.map((reportee, idx) => (
                                        <div key={idx} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 flex items-center space-x-3 shadow dark:shadow-gray-900">
                                            <div className="w-10 h-10 bg-gray-300 dark:bg-gray-700 rounded-full flex items-center justify-center">
                                                <User className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-white">{reportee.name}</p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">{reportee.role}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end mt-6">
                        <button
                            onClick={() => setShowMatrixModal(false)}
                            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </>
        );
    };

    // Detailed Profile Modal
    const DetailedProfileModal = () => {
        if (!showDetailedProfile || !selectedEmployee) return null;

        return (
            <>
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />

                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900 p-6 z-50 max-w-lg w-full max-h-[80vh] overflow-y-auto">
                    <div className="flex items-start space-x-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="w-20 h-20 bg-gray-300 dark:bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="w-10 h-10 text-gray-600 dark:text-gray-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{selectedEmployee.name}</h2>
                            <p className="text-gray-600 dark:text-gray-400">
                                {selectedEmployee.role} | {selectedEmployee.department || 'N/A'}
                            </p>
                        </div>
                    </div>

                    <div className="mt-6 space-y-6">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Details</h3>

                            <div className="mb-4">
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Employment Details</h4>
                                <div className="space-y-2 pl-4">
                                    <div className="flex">
                                        <span className="text-sm text-gray-600 dark:text-gray-400 w-40">Official Email ID:</span>
                                        <span className="text-sm text-gray-900 dark:text-gray-100">{selectedEmployee.email}</span>
                                    </div>
                                    <div className="flex">
                                        <span className="text-sm text-gray-600 dark:text-gray-400 w-40">Employee ID:</span>
                                        <span className="text-sm text-gray-900 dark:text-gray-100">{selectedEmployee.employeeId || 'N/A'}</span>
                                    </div>
                                    {selectedEmployee.directReports > 0 && (
                                        <div className="flex">
                                            <span className="text-sm text-gray-600 dark:text-gray-400 w-40">Reportees:</span>
                                            <span className="text-sm text-gray-900 dark:text-gray-100">
                                                {selectedEmployee.directReports} Direct | {selectedEmployee.totalReports} Total
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mb-4">
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Employee Lifecycle Events</h4>
                                <div className="space-y-2 pl-4">
                                    <div className="flex">
                                        <span className="text-sm text-gray-600 dark:text-gray-400 w-40">Date of Joining:</span>
                                        <span className="text-sm text-gray-900 dark:text-gray-100">{selectedEmployee.dateOfJoining || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4">
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Personal Information</h4>
                                <div className="space-y-2 pl-4">
                                    <div className="flex">
                                        <span className="text-sm text-gray-600 dark:text-gray-400 w-40">Office Location:</span>
                                        <span className="text-sm text-gray-900 dark:text-gray-100">{selectedEmployee.officeLocation || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                            onClick={() => setShowDetailedProfile(false)}
                            className="px-8 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </>
        );
    };

    const EmployeeCard = ({ employee, level = 0, isChild = false }) => {
        if (!isEmployeeVisible(employee)) return null;

        const isExpanded = expandedNodes[employee.id];
        const hasSubordinates = employee.subordinates && employee.subordinates.length > 0;
        const isHovered = hoveredEmployee === employee.id;
        const isHighlighted = highlightedEmployee === employee.id;
        const isSelected = selectedEmployee?.id === employee.id;

        const visibleSubordinates = hasSubordinates
            ? employee.subordinates.filter(sub => isEmployeeVisible(sub))
            : [];

        // Constants for layout calculations
        const CARD_WIDTH = 280;
        const CARD_GAP = 32;
        const CARD_HEIGHT = 140;
        const VERTICAL_GAP = 48;

        // Calculate total width needed for children to prevent recentering
        const childrenTotalWidth = visibleSubordinates.length > 0
            ? (CARD_WIDTH + CARD_GAP) * visibleSubordinates.length - CARD_GAP
            : CARD_WIDTH;

        return (
            <div className="relative inline-flex flex-col items-center" style={{
                minWidth: isExpanded ? `${Math.max(CARD_WIDTH, childrenTotalWidth)}px` : `${CARD_WIDTH}px`
            }}>
                {/* Line from parent to this card */}
                {isChild && (
                    <div
                        className="absolute w-0.5 bg-gray-300 dark:bg-gray-600 z-0"
                        style={{
                            left: '50%',
                            transform: 'translateX(-50%)',
                            top: `-${VERTICAL_GAP}px`,
                            height: `${VERTICAL_GAP}px`
                        }}
                    />
                )}

                {/* Employee Card */}
                <div
                    id={`employee-${employee.id}`}
                    className={`relative bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-4 cursor-pointer transition-all transform hover:scale-105 z-10 ${
                        isHovered || isSelected || isExpanded ?
                            'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-2xl' :
                            isHighlighted ? 'ring-4 ring-yellow-400 shadow-2xl' :
                                'shadow-md hover:shadow-xl'
                    }`}
                    style={{
                        width: `${CARD_WIDTH}px`,
                        height: `${CARD_HEIGHT}px`,
                        boxShadow: isHovered || isSelected || isExpanded ?
                            '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' :
                            '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={() => setHoveredEmployee(employee.id)}
                    onMouseLeave={() => setHoveredEmployee(null)}
                    onClick={(e) => handleEmployeeCardClick(employee, e)}
                >
                    <div className="flex items-start space-x-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isHovered || isSelected || isExpanded ? 'bg-white/20' : 'bg-gray-300 dark:bg-gray-700'
                        }`}>
                            {employee.avatar ? (
                                <img src={employee.avatar} alt={employee.name} className="w-full h-full rounded-full" />
                            ) : (
                                <User className={`w-6 h-6 ${isHovered || isSelected || isExpanded ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`} />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className={`font-semibold truncate ${isHovered || isSelected || isExpanded ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                                {employee.name}
                            </h3>
                            <p className={`text-sm truncate ${isHovered || isSelected || isExpanded ? 'text-white/90' : 'text-gray-600 dark:text-gray-400'}`}>
                                {employee.role}
                            </p>
                            {employee.department && (
                                <p className={`text-sm truncate ${isHovered || isSelected || isExpanded ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                                    {employee.department}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className={`mt-3 pt-3 border-t flex items-center justify-between ${
                        isHovered || isSelected || isExpanded ? 'border-white/20' : 'border-gray-200 dark:border-gray-700'
                    }`}>
                        <div className={`text-xs ${
                            isHovered || isSelected || isExpanded ? 'text-white' : 'text-gray-600 dark:text-gray-400'
                        }`}>
                            <span>{employee.directReports || 0} Direct | {employee.totalReports || 0} Total Reportees</span>
                        </div>
                        {hasSubordinates && (
                            <button
                                onClick={(e) => toggleExpand(employee.id, e)}
                                className={`p-1 rounded hover:bg-opacity-20 hover:bg-black transition-colors ${
                                    isHovered || isSelected || isExpanded ? 'text-white' : 'text-gray-600 dark:text-gray-400'
                                }`}
                            >
                                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                        )}
                    </div>
                </div>

                {/* Children container */}
                {isExpanded && visibleSubordinates.length > 0 && (
                    <div className="relative w-full" style={{ marginTop: `${VERTICAL_GAP}px` }}>
                        {/* Vertical line from parent to horizontal connector */}
                        <div
                            className="absolute w-0.5 bg-gray-300 dark:bg-gray-600 z-0"
                            style={{
                                left: '50%',
                                transform: 'translateX(-50%)',
                                top: `-${VERTICAL_GAP}px`,
                                height: `${VERTICAL_GAP}px`
                            }}
                        />

                        {/* Horizontal line connecting children */}
                        {visibleSubordinates.length > 1 && (
                            <div
                                className="absolute h-0.5 bg-gray-300 dark:bg-gray-600 z-0"
                                style={{
                                    top: '0',
                                    left: '50%',
                                    transform: `translateX(-${childrenTotalWidth / 2}px)`,
                                    width: `${childrenTotalWidth}px`
                                }}
                            />
                        )}

                        {/* Children cards */}
                        <div className="flex justify-center" style={{
                            gap: `${CARD_GAP}px`,
                            marginTop: `${VERTICAL_GAP}px`
                        }}>
                            {visibleSubordinates.map((sub) => (
                                <EmployeeCard key={sub.id} employee={sub} level={level + 1} isChild={true} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const chartScrollStyle = {
        overflowX: 'auto',
        overflowY: 'auto',
        height: isFullscreen ? '100vh' : 'calc(100vh - 200px)',
        position: 'relative'
    };

    return (
        <div ref={fullscreenRef} className={`min-h-screen bg-gray-100 dark:bg-gray-900 ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
            {!isFullscreen && (
                <div className="bg-white dark:bg-gray-800 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            {/*<select*/}
                            {/*    value={viewAs}*/}
                            {/*    onChange={(e) => setViewAs(e.target.value)}*/}
                            {/*    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"*/}
                            {/*>*/}
                            {/*    <option>View As: Admin</option>*/}
                            {/*    <option>View As: Manager</option>*/}
                            {/*    <option>View As: Employee</option>*/}
                            {/*</select>*/}

                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyPress={handleSearchKeyPress}
                                    className="pl-4 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                    onClick={executeSearch}
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
                                >
                                    <Search className="w-4 h-4 text-gray-400" />
                                </button>
                            </div>

                            <select
                                value={selectedLegalEntity}
                                onChange={(e) => setSelectedLegalEntity(e.target.value)}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="all">All Legal Entities</option>
                                {legalEntities.map((entity) => (
                                    <option key={entity.id} value={entity.id}>
                                        {entity.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center space-x-4">
                            <div className="relative">
                                <button
                                    onClick={() => setShowFilters(!showFilters)}
                                    className="flex items-center space-x-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border border-gray-300 dark:border-gray-600 rounded-lg"
                                >
                                    <Filter size={16} />
                                    <span className="text-sm">Filters</span>
                                    {selectedFilters.length > 0 && (
                                        <span className="ml-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                                            {selectedFilters.length}
                                        </span>
                                    )}
                                </button>
                                <FilterDropdown />
                            </div>

                            {/*<button*/}
                            {/*    onClick={handleDownload}*/}
                            {/*    className="flex items-center space-x-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"*/}
                            {/*>*/}
                            {/*    <Download size={16} />*/}
                            {/*    <span className="text-sm">Download</span>*/}
                            {/*</button>*/}

                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Detailed View</span>
                                <button
                                    onClick={() => setDetailedView(!detailedView)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                        detailedView ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                                    }`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                            detailedView ? 'translate-x-6' : 'translate-x-1'
                                        }`}
                                    />
                                </button>
                            </div>

                            <div className="flex items-center space-x-1 border-l border-gray-300 dark:border-gray-600 pl-4">
                                <button
                                    onClick={handleZoomOut}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                    title="Zoom Out"
                                >
                                    <Minus className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                </button>
                                <button
                                    onClick={handleZoomIn}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                    title="Zoom In"
                                >
                                    <Plus className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                </button>
                                <button
                                    onClick={handleFullscreen}
                                    className={`p-2 rounded transition-colors ${
                                        isFullscreen ? 'bg-blue-500 text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                                    title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                                >
                                    {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isFullscreen && (
                <div className="absolute top-4 right-4 z-50">
                    <button
                        onClick={handleFullscreen}
                        className="p-2 bg-blue-500 text-white rounded-lg shadow-lg hover:bg-blue-600"
                        title="Exit Fullscreen"
                    >
                        <Minimize2 className="w-5 h-5" />
                    </button>
                </div>
            )}

            <div ref={chartContainerRef} className="p-8 overflow-visible" style={chartScrollStyle}>
                {employeeNotFound ? (
                    <div className="flex flex-col items-center justify-center min-h-[400px]">
                        <UserX className="w-16 h-16 text-gray-400 mb-4" />
                        <h3 className="text-xl font-medium text-gray-700 dark:text-gray-300 mb-2">Employee Not Found</h3>
                        <p className="text-gray-500 dark:text-gray-400">No employee found matching "{searchQuery}"</p>
                    </div>
                ) : !organizationData.ceo && organizationData.peers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center min-h-[400px]">
                        <UserX className="w-16 h-16 text-gray-400 mb-4" />
                        <h3 className="text-xl font-medium text-gray-700 dark:text-gray-300 mb-2">No Employees Found</h3>
                        <p className="text-gray-500 dark:text-gray-400">
                            {selectedLegalEntity === 'all'
                                ? 'No employees in the organization yet'
                                : `No employees found in ${legalEntities.find(le => le.id === selectedLegalEntity)?.name || 'selected legal entity'}`}
                        </p>
                    </div>
                ) : (
                    <div className="inline-block" style={{
                        transform: `scale(${zoomLevel})`,
                        transformOrigin: 'top left',
                        minWidth: '100%'
                    }}>
                        <div className="flex flex-col items-center">
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:shadow-gray-900 px-6 py-3 mb-8"
                                 style={{
                                     boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                                 }}>
                                <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">{organizationData.name}</h2>
                            </div>

                            <div className="relative">
                                <div className="w-0.5 h-12 bg-gray-300 dark:bg-gray-600 mx-auto"></div>
                            </div>

                            {(() => {
                                // Build list of all top-level employees
                                const allTopEmployees = [
                                    ...organizationData.additionalRoles,
                                    ...organizationData.peers,
                                    organizationData.ceo
                                ].filter(emp => emp !== null && emp !== undefined);

                                const topEmployees = allTopEmployees;
                                const visibleEmployees = topEmployees.filter(emp => isEmployeeVisible(emp));
                                const numEmployees = visibleEmployees.length;
                                const lineWidth = numEmployees > 1 ?
                                    (280 + 32) * numEmployees - 32 :  // CARD_WIDTH + GAP for each card, minus last gap
                                    0;

                                return (
                                    <>
                                        {numEmployees > 1 && (
                                            <div className="relative h-0.5 bg-gray-300 dark:bg-gray-600" style={{ width: `${lineWidth}px` }} />
                                        )}

                                        <div className="flex justify-center" style={{
                                            gap: '32px',
                                            paddingTop: '48px'
                                        }}>
                                            {visibleEmployees.map(employee => (
                                                <EmployeeCard key={employee.id} employee={employee} isChild={true} />
                                            ))}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                )}
            </div>

            <QuickProfileModal />
            <MatrixManagerModal />
            <DetailedProfileModal />
        </div>
    );
};

export default Organization;